import { OpenLink } from 'app/design/icons/open-link';
import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Link } from 'app/design/primitives/link';
import { Text } from 'app/design/primitives/text';

import { getPlaceDayKey, PLACE_DAY_ORDER } from 'app/lib/opening-hours';
import type { SharingPlaceDetails } from 'app/types/sharing-place';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface Properties {
  open: boolean;
  onClose: () => void;
  placeDetails: SharingPlaceDetails;
}

export function OpeningHoursBottomSheet({ open, onClose, placeDetails }: Properties) {
  const { t } = useTranslation();
  const todayKey = getPlaceDayKey();

  return (
    <BottomSheet open={open} onClose={onClose}>
      <Column
        gap="spacing.general.x4"
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x5'],
          paddingBottom: theme.spacing['spacing.general.x12'],
        }}
      >
        <Text fontWeight="700" variant="headerXSmall">
          {t('place.openingHoursTitle', { ns: 'neighbour-profile' })}
        </Text>

        {/* Info box */}
        <Row
          alignItems="flex-start"
          gap="spacing.general.x2"
          style={{
            backgroundColor: '#EEF0FF',
            borderRadius: theme.borderRadii['radius.md'],
            padding: theme.spacing['spacing.general.x3'],
          }}
        >
          <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={32} color={theme.colors['surface.brand.default']} />
          <Text color="text.primary" flex={1} variant="bodyBase">
            {t('place.openingHoursDisclaimer', { ns: 'neighbour-profile' })}
          </Text>
        </Row>

        {/* Website link */}
        {placeDetails.websiteUrl ? (
          <Link href={placeDetails.websiteUrl} variant="bodyBase">
            <Row alignItems="center" gap="spacing.general.x1">
              <Text
                color="text.primary"
                variant="bodyBase"
                style={{ color: theme.colors['text.brand'], fontWeight: '700' }}
              >
                {t('place.viewHoursOnWebsite', { ns: 'neighbour-profile' })}
              </Text>
              <OpenLink color={theme.colors['text.secondary']} width={14} height={14} />
            </Row>
          </Link>
        ) : null}

        {/* Day list */}
        {placeDetails.openingHours
          ? PLACE_DAY_ORDER.map((day) => (
              <Row alignItems="center" justifyContent="space-between" key={day} opacity={day === todayKey ? 1 : 0.6}>
                <Text color="text.primary" fontWeight={day === todayKey ? '700' : '400'} variant="bodyBase">
                  {t(`days.${day}`, { ns: 'neighbour-profile' })}
                </Text>
                <Text
                  color={placeDetails.openingHours?.[day] ? 'text.primary' : 'icon.tertiary'}
                  fontWeight={day === todayKey ? '700' : '400'}
                  variant="bodyBase"
                >
                  {placeDetails.openingHours?.[day] ?? t('place.closed', { ns: 'neighbour-profile' })}
                </Text>
              </Row>
            ))
          : null}
      </Column>
    </BottomSheet>
  );
}
